
import { defineComponent, onBeforeMount, reactive } from 'vue'

// @ui-kit
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewDatePicker from '@/ui-kit/NewDatePicker/index.vue'
import NewCheckbox from '@/common/new-components/NewCheckbox/index.vue'

import { CityT } from '@/core/types/cities.types'

import locationAPI from '@/new-services/location.service'

import useVuelidate from '@vuelidate/core'
import { dateRangeMin } from '@/common/validators/common.validators'
import { useDate } from '@/common/composable/useDate'
import { UserT } from '@/core/types/users.types'
import { users } from '@/core/realisations/users.realisation'

const STATUSES = [
    { id: 2, name: 'Рейс, который уехал по графику' },
    { id: 3, name: 'Предрейс без рейса' },
    { id: 4, name: 'Рейс уехал без графика' },
    { id: 5, name: 'Перенесенный предрейс' },
    { id: 6, name: 'Перенесен с опозданием' },
]

export default defineComponent({
    components: {
        NewSelect,
        NewButton,
        NewDatePicker,
        NewCheckbox,
    },
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const { getThisWeek, getPreviousWeek, getNextWeek } = useDate()

        const filters = reactive({
            period: [] as string[],
            fromCity: null as number | null,
            whereCity: null as number | null,
            statusId: null as number | null,
            logistId: null as number | null,
        })

        const state_checkbox = reactive({
            is_this_week: false,
            is_previous_week: false,
            is_next_week: false,
        })

        const filters$ = useVuelidate(
            {
                period: { dateRangeMin },
            },
            filters
        )

        const data_state = reactive({
            cities: {
                is_loading: false,
                is_failed: false,
                list: [] as CityT[],
            },
            logists: {
                is_loading: false,
                is_failed: false,
                list: [] as UserT[],
            },
        })

        const loadCities = async () => {
            try {
                data_state.cities.is_loading = true

                const response = await locationAPI.getCities()
                data_state.cities.list = response.data.data

                data_state.cities.is_failed = false
            } catch (error) {
                data_state.cities.is_failed = true
                console.error(error)
            } finally {
                data_state.cities.is_loading = false
            }
        }

        const loadLogists = async () => {
            try {
                data_state.logists.is_loading = true

                const params = {
                    page: 1,
                    limit: 9999,
                    roleId: 21,
                    statusId: 1,
                }
                const data = await users.getList(params)

                data_state.logists.list = data.data

                data_state.logists.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.logists.is_failed = true
            } finally {
                data_state.logists.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadCities()
            loadLogists()
        })

        const onSubmit = () => {
            if (props.disabled || props.loading) return

            filters$.value.$touch()
            if (filters$.value.$invalid) return

            emit('submit', {
                period: filters.period,
                fromCity: filters.fromCity,
                whereCity: filters.whereCity,
                statusId: filters.statusId,
                logistId: filters.logistId,
            })
        }

        const resetCheckboxes = (type?: string) => {
            if (type !== 'this_week') state_checkbox.is_this_week = false
            if (type !== 'previous_week') state_checkbox.is_previous_week = false
            if (type !== 'next_week') state_checkbox.is_next_week = false
        }

        const onReset = () => {
            filters.period = []
            filters.fromCity = null
            filters.whereCity = null
            filters.statusId = null
            filters.logistId = null

            resetCheckboxes()

            filters$.value.$reset()

            emit('reset')
        }

        const manageThisWeek = () => {
            if (state_checkbox.is_this_week) {
                const { monday, sunday } = getThisWeek()

                filters.period.push(monday)
                filters.period.push(sunday)
            } else {
                filters.period = []
            }
        }

        const managePreviousWeek = () => {
            if (state_checkbox.is_previous_week) {
                const { previousMonday, previousSunday } = getPreviousWeek()

                filters.period.push(previousMonday)
                filters.period.push(previousSunday)
            } else {
                filters.period = []
            }
        }

        const manageNextWeek = () => {
            if (state_checkbox.is_next_week) {
                const { nextMonday, nextSunday } = getNextWeek()

                filters.period.push(nextMonday)
                filters.period.push(nextSunday)
            } else {
                filters.period = []
            }
        }

        const onCheckboxChange = (type: string) => {
            filters.period = []
            resetCheckboxes(type)

            if (type === 'this_week') manageThisWeek()
            else if (type === 'previous_week') managePreviousWeek()
            else if (type === 'next_week') manageNextWeek()
        }

        return {
            filters,
            filters$,
            state_checkbox,
            data_state,
            loadCities,
            onSubmit,
            onReset,
            onCheckboxChange,
            loadLogists,
            STATUSES,
        }
    },
})
